import React, { useState, useEffect } from 'react';
import ChatService from '../services/ChatService';
import { theme } from '../styles/theme';
import './CreateChatScreen.css';

/**
 * CreateChatScreen - Screen for creating new chat rooms
 * Matches Flutter CreateChatScreen functionality
 */
const CreateChatScreen = ({
  currentUserId,
  currentUserName,
  currentUserRole,
  currentAgencyId,
  onBack,
  onChatRoomCreated
}) => {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadAvailableUsers();
  }, [currentUserId, currentUserRole, currentAgencyId]);

  useEffect(() => {
    // Filter users based on search query
    if (searchQuery.trim() === '') {
      setFilteredUsers(availableUsers);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = availableUsers.filter(user =>
        user.userName.toLowerCase().includes(query) ||
        user.userRole.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, availableUsers]);

  const loadAvailableUsers = async () => {
    try {
      setIsLoading(true);
      const users = [];

      // Process users based on current user role
      if (currentUserRole === 'driver') {
        // Driver can only chat with admin
        // Load admin from userData (stored in localStorage)
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const agencyInfo = userData.agencyInfo || userData.agencyDetails || {};
        const agencyName = agencyInfo.full_name || agencyInfo.agencyName || agencyInfo.agency_name || 'Admin';
        const agencyId = agencyInfo.agency_id || agencyInfo.agencyId || currentAgencyId;

        users.push({
          userId: `admin_${agencyId}`,
          userName: agencyName,
          userRole: 'admin',
          agencyId: agencyId.toString()
        });
      } else if (currentUserRole === 'officeAdmin') {
        // Office admin can chat with drivers, other office admins, AND admin
        // Use the new API to get drivers and office admins (role 4 and 6)
        const driverList = await ChatService.getDriversByRole(currentAgencyId);
        // Process drivers and office admins from API response
        let driversCount = 0;
        let officeAdminsCount = 0;
        
        driverList.forEach(driver => {
          const userId = driver.user_id?.toString();
          const agencyId = driver.agency_id?.toString() || currentAgencyId;
          
          // Get driver/office admin name - API provides full_name
          // Use fallback if name is missing
          const driverName = driver.full_name || 
            `${driver.first_name || ''} ${driver.middle_name || ''} ${driver.last_name || ''}`.trim() ||
            driver.login_user_name ||
            `User ${userId}`; // Fallback if no name

          // Map role from API: "Driver" | "Office Admin" (or legacy user_role 4 | 6)
          let userRoleString = 'driver';
          const apiRole = (driver.role || '').trim().toLowerCase();
          if (apiRole === 'driver' || driver.user_role === 4) {
            userRoleString = 'driver';
            driversCount++;
          } else if (apiRole === 'office admin' || driver.user_role === 6) {
            userRoleString = 'officeAdmin';
            officeAdminsCount++;
          } else if (apiRole === 'admin' || apiRole === 'administrator') {
            userRoleString = 'admin';
          } else if (driver.role) {
            // API returned a role we didn't map; normalize for display (e.g. "Office Admin" -> officeAdmin)
            userRoleString = apiRole.replace(/\s+/g, '') === 'officeadmin' ? 'officeAdmin' : apiRole;
          }

          // Include user even if name is missing (use fallback)
          if (userId && agencyId) {
            users.push({
              userId,
              userName: driverName || `User ${userId}`,
              userRole: userRoleString,
              agencyId
            });
          } else {
            console.warn('⚠️ Skipping user due to missing data:', {
              userId,
              agencyId,
              role: driver.role,
              user_role: driver.user_role,
              hasName: !!driverName
            });
          }
        });
        
        // Also add admin user
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const agencyInfo = userData.agencyInfo || userData.agencyDetails || {};
        const agencyName = agencyInfo.full_name || agencyInfo.agencyName || agencyInfo.agency_name || 'Admin';
        const agencyId = agencyInfo.agency_id || agencyInfo.agencyId || currentAgencyId;

        users.push({
          userId: `admin_${agencyId}`,
          userName: agencyName,
          userRole: 'admin',
          agencyId: agencyId.toString()
        });
        
        // CRITICAL: For officeAdmin users, filter out other office admins from the list
        // Office admins should NOT be able to create chats with other office admins
        const usersBeforeOfficeAdminFilter = users.length;
        const usersAfterOfficeAdminFilter = users.filter(user => {
          // Keep all users except other office admins
          if (user.userRole === 'officeAdmin') {
            console.log(`🚫 Filtering out office admin from Create Chat list: ${user.userName} (ID: ${user.userId})`);
            return false;
          }
          return true;
        });
        
        if (usersBeforeOfficeAdminFilter !== usersAfterOfficeAdminFilter.length) {
          console.log(`📊 Office Admin Filter in Create Chat: ${usersBeforeOfficeAdminFilter} → ${usersAfterOfficeAdminFilter.length} (removed ${usersBeforeOfficeAdminFilter - usersAfterOfficeAdminFilter.length} office admins)`);
        }
        
        // Replace users array with filtered version
        users.length = 0;
        users.push(...usersAfterOfficeAdminFilter);
      } else if (currentUserRole === 'admin') {
        // Admin can chat with drivers AND office admins
        // Use the new API to get drivers and office admins (role 4 and 6)
        const driverList = await ChatService.getDriversByRole(currentAgencyId);
        
     

        // Process drivers and office admins from API response
        let driversCount = 0;
        let officeAdminsCount = 0;
        
        driverList.forEach(driver => {
          const userId = driver.user_id?.toString();
          const agencyId = driver.agency_id?.toString() || currentAgencyId;
          
          // Get driver/office admin name - API provides full_name
          // Use fallback if name is missing
          const driverName = driver.full_name || 
            `${driver.first_name || ''} ${driver.middle_name || ''} ${driver.last_name || ''}`.trim() ||
            driver.login_user_name ||
            `User ${userId}`; // Fallback if no name

          // Map role from API: "Driver" | "Office Admin" (or legacy user_role 4 | 6)
          let userRoleString = 'driver';
          const apiRole = (driver.role || '').trim().toLowerCase();
          if (apiRole === 'driver' || driver.user_role === 4) {
            userRoleString = 'driver';
            driversCount++;
          } else if (apiRole === 'office admin' || driver.user_role === 6) {
            userRoleString = 'officeAdmin';
            officeAdminsCount++;
          } else if (apiRole === 'admin' || apiRole === 'administrator') {
            userRoleString = 'admin';
          } else if (driver.role) {
            userRoleString = apiRole.replace(/\s+/g, '') === 'officeadmin' ? 'officeAdmin' : apiRole;
          }

          // Include user even if name is missing (use fallback)
          if (userId && agencyId) {
            users.push({
              userId,
              userName: driverName || `User ${userId}`,
              userRole: userRoleString,
              agencyId
            });
          } else {
            console.warn('⚠️ Skipping user due to missing data:', {
              userId,
              agencyId,
              role: driver.role,
              user_role: driver.user_role,
              hasName: !!driverName
            });
          }
        });
        
  
      }

      // Filter out current user
      const userIdForChat = currentUserRole === 'admin' 
        ? `admin_${currentAgencyId}` 
        : currentUserId;
      
      const filteredUsers = users.filter(user => {
        const isCurrentUser = user.userId === currentUserId || user.userId === userIdForChat;
        return !isCurrentUser;
      });

     
      // Filter out users who already have active chat rooms
      const existingRooms = await ChatService.getChatRooms(
        userIdForChat,
        currentAgencyId,
        currentUserRole
      );
      
      // Get list of user IDs that already have chat rooms
      const existingUserIds = new Set();
      const existingRoomsDetails = [];
      
      existingRooms.forEach(room => {
        const participants = room.participants || [];
        const searchUserId = String(userIdForChat || '');
        
        // Find all participants that are NOT the current user
        // Handle different ID formats (string, number, admin format)
        const otherParticipants = participants.filter(p => {
          const pUserId = String(p.userId || p.user_id || '');
          const pUserIdNum = Number(p.userId || p.user_id);
          
          // Check multiple formats to ensure we don't match current user
          const isCurrentUser = 
            pUserId === searchUserId ||
            (pUserIdNum && !isNaN(pUserIdNum) && String(pUserIdNum) === searchUserId) ||
            (currentUserRole === 'admin' && (
              pUserId === `admin_${currentAgencyId}` ||
              pUserId === '1' ||
              pUserIdNum === 1
            ));
          
          return !isCurrentUser;
        });
        
        otherParticipants.forEach(otherParticipant => {
          // Get user ID in multiple formats for matching
          const otherUserId = String(otherParticipant.userId || otherParticipant.user_id || '');
          const otherUserIdNum = otherParticipant.userId || otherParticipant.user_id;
          
          // Add both string and number formats to ensure matching
          existingUserIds.add(otherUserId);
          if (otherUserIdNum && !isNaN(Number(otherUserIdNum))) {
            existingUserIds.add(String(otherUserIdNum));
            existingUserIds.add(Number(otherUserIdNum).toString());
          }
          
          existingRoomsDetails.push({
            roomId: room.roomId,
            otherUserId: otherUserId,
            otherUserName: otherParticipant.userName,
            otherUserRole: otherParticipant.userRole,
            participantCount: participants.length
          });
        });
      });
      
    
      // Remove users who already have chat rooms
      // Check both exact match and string conversion for ID matching
      const finalUsers = filteredUsers.filter(user => {
        const userStrId = String(user.userId);
        const userNumId = user.userId ? Number(user.userId) : null;
        
        // Check if user ID exists in existing chats (as string or number)
        const hasExistingChat = existingUserIds.has(userStrId) || 
                               (userNumId && !isNaN(userNumId) && existingUserIds.has(String(userNumId))) ||
                               existingUserIds.has(userNumId?.toString());
        
        if (hasExistingChat) {
          const matchingRoom = existingRoomsDetails.find(r => 
            String(r.otherUserId) === userStrId || 
            String(r.otherUserId) === String(userNumId)
          );
          console.log(`🚫 Filtering out ${user.userName} (${user.userRole}, ID: ${userStrId}) - already has chat room`, 
            matchingRoom ? `(Room: ${matchingRoom.roomId})` : '');
        }
        return !hasExistingChat;
      });

     

      setAvailableUsers(finalUsers);
      setFilteredUsers(finalUsers);
    } catch (error) {
      console.error('❌ Error loading available users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateChatRoom = async (selectedUser) => {
    try {
      setIsCreating(true);

      // Normalize admin IDs - ChatService.getOrCreateRoom will handle sorting
      // but we should normalize here for consistency
      let currentUser = {
        userId: currentUserId,
        userName: currentUserName,
        userRole: currentUserRole,
        agencyId: currentAgencyId
      };

      // If current user is admin, ensure constructed ID format
      if (currentUserRole === 'admin' && !currentUserId.startsWith('admin_')) {
        currentUser.userId = `admin_${currentAgencyId}`;
      }

      // Normalize selected user if it's an admin
      let normalizedSelectedUser = { ...selectedUser };
      if (selectedUser.userRole === 'admin' && !selectedUser.userId.startsWith('admin_')) {
        normalizedSelectedUser.userId = `admin_${selectedUser.agencyId}`;
      }
      // ChatService.getOrCreateRoom will:
      // 1. Normalize admin IDs (if not already normalized)
      // 2. Sort user IDs alphabetically
      // 3. Send to backend in consistent order
      // This ensures room ID is always the same regardless of who initiates
     
      
      const room = await ChatService.getOrCreateRoom(currentUser, normalizedSelectedUser);

      if (room) {
        if (onChatRoomCreated) {
          onChatRoomCreated(room);
        }
        if (onBack) {
          onBack();
        }
      } else {
       
        alert('Failed to create chat room. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error creating chat room:', error);
      alert('Failed to create chat room');
    } finally {
      setIsCreating(false);
    }
  };

  const getAvatarColor = (role) => {
    const r = (role || '').toLowerCase().replace(/\s+/g, '');
    switch (r) {
      case 'admin':
      case 'administrator':
        return theme.colors.roleAdmin;
      case 'officeadmin':
        return theme.colors.roleOfficeAdmin;
      case 'driver':
        return theme.colors.roleDriver;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getRoleDisplay = (role) => {
    if (role == null || role === '') return 'User';
    const r = role.toLowerCase().replace(/\s+/g, '');
    switch (r) {
      case 'administrator':
        return 'Administrator';
      case 'admin':
        return 'Admin';
      case 'officeadmin':
        return 'Office Admin';
      case 'driver':
        return 'Driver';
      default:
        return role || 'User';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <div className="create-chat-screen" style={{ backgroundColor: theme.colors.background }}>
      {/* Header */}
      <div className="create-chat-header" style={{ backgroundColor: theme.colors.greenButton }}>
        <button className="back-button" onClick={onBack}>←</button>
        <div className="header-title">New Chat</div>
      </div>

      {/* Search Bar */}
      <div className="create-chat-search">
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {/* User List */}
      <div className="create-chat-list">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-users">
            <div className="empty-icon">👤</div>
            <div className="empty-title">
              {searchQuery ? 'No users found' : 'No users available'}
            </div>
            <div className="empty-subtitle">
              {searchQuery 
                ? 'Try a different search term' 
                : 'All users already have active chats'}
            </div>
          </div>
        ) : (
          filteredUsers.map((user, index) => (
            <div
              key={`${user.userId}-${index}`}
              className="user-list-item"
              onClick={() => !isCreating && handleCreateChatRoom(user)}
              style={{
                opacity: isCreating ? 0.6 : 1,
                cursor: isCreating ? 'not-allowed' : 'pointer'
              }}
            >
              <div
                className="user-avatar"
                style={{ backgroundColor: getAvatarColor(user?.userRole) }}
              >
                <span>{getInitials(user.userName)}</span>
              </div>
              <div className="user-info">
                <div className="user-name">{user.userName}</div>
                <div className="user-role">{getRoleDisplay(user?.userRole)}</div>
              </div>
              <div className="user-arrow">›</div>
            </div>
          ))
        )}
      </div>

      {isCreating && (
        <div className="creating-overlay">
          <div className="creating-spinner"></div>
          <div>Creating chat...</div>
        </div>
      )}
    </div>
  );
};

export default CreateChatScreen;

